/**
 * EngineMount — 声明式续渲挂载点。
 *
 * 布局插件在 Renderer 中放置此组件，由宿主环境通过
 * ContinuationContext 注入实际渲染能力。脱离宿主环境时
 * 输出友好错误占位。
 *
 * 用法：
 * ```tsx
 * import { EngineMount } from "@magic-md/plugin-sdk";
 *
 * <EngineMount target={{ kind: "definition", id: "myDef" }} />
 * ```
 */

import { createContext, useContext } from "react";
import type { ContinuationContextValue, EngineMountProps } from "../types";
import { usePluginSdkT } from "../i18n";
import { getPluginToneStyle } from "./theme-helpers";

const CONTINUATION_CONTEXT_KEY = Symbol.for(
    "magic-md.plugin-sdk.continuation-context",
);

type ContinuationContextGlobal = typeof globalThis & {
    [CONTINUATION_CONTEXT_KEY]?: React.Context<ContinuationContextValue | null>;
};

function getSharedContinuationContext(): React.Context<ContinuationContextValue | null> {
    const globalScope = globalThis as ContinuationContextGlobal;
    const existing = globalScope[CONTINUATION_CONTEXT_KEY];
    if (existing) return existing;

    const created = createContext<ContinuationContextValue | null>(null);
    globalScope[CONTINUATION_CONTEXT_KEY] = created;
    return created;
}

export const ContinuationContext = getSharedContinuationContext();

/**
 * ContinuationProvider — 宿主环境注入续渲能力。
 *
 * 在 PluginRendererWrapper（preview pipeline）和
 * RenderBlock（document-renderer pipeline）中包裹插件 Renderer，
 * 为其中的 EngineMount 提供 resolve 函数。
 */
export const ContinuationProvider = ContinuationContext.Provider;

/**
 * EngineMount — 布局插件的唯一内容注入点。
 *
 * 接收一个 ContinuationTarget，通过 ContinuationContext
 * 交由平台 ContinuationResolver 完成渲染。
 */
export function EngineMount({ target, className, fallback }: EngineMountProps) {
    const t = usePluginSdkT();
    const ctx = useContext(ContinuationContext);

    if (!ctx) {
        // 脱离宿主环境（SDK Preview、单元测试等）→ 友好错误占位
        return (
            fallback ?? (
                <div
                    className="rounded border border-dashed p-2 text-center text-[10px]"
                    style={getPluginToneStyle("warning", "banner")}
                >
                    {t("engineMount.requiresHost")}
                </div>
            )
        );
    }

    const mode = target.mode ?? ctx.defaultMode ?? "block";
    const resolvedTarget = target.mode ? target : { ...target, mode };
    const Host = mode === "inline" ? "span" : "div";

    return (
        <Host
            className={className}
            data-continuation-kind={target.kind}
            data-continuation-mode={mode}
        >
            {ctx.resolve(resolvedTarget)}
        </Host>
    );
}
